import { useState, useCallback } from 'react';
import { ProductResult } from '../types/product';
import { searchProducts } from '../services/searchApi';

interface UseProductSearchReturn {
  products: ProductResult[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  message: string;
  suggestedQuery: string | null;
  search: (query: string, sort?: string) => Promise<void>;
}

/**
 * 상품 검색 커스텀 훅.
 * 검색 상태 관리와 API 호출을 캡슐화한다.
 */
export function useProductSearch(): UseProductSearchReturn {
  const [products, setProducts] = useState<ProductResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [message, setMessage] = useState('');
  const [suggestedQuery, setSuggestedQuery] = useState<string | null>(null);

  const search = useCallback(async (query: string, sort = 'sim') => {
    if (!query.trim()) {
      setError('검색어를 입력해주세요.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await searchProducts(query, sort);

      if (result.error) {
        setError(result.message);
        setProducts([]);
        setTotalCount(0);
      } else if (result.data) {
        setProducts(result.data.products);
        setTotalCount(result.data.total);
        setMessage(result.message);
        setSuggestedQuery(result.data.suggestedQuery || null);
      }
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : '검색 중 오류가 발생했습니다.';
      setError(errorMsg);
      setProducts([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { products, isLoading, error, totalCount, message, suggestedQuery, search };
}
